import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Chip, Stack, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, CircularProgress, Divider, Grid,
} from '@mui/material';
import {
  AccountTree as BranchIcon,
  AddBusiness as AddIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Pause as SuspendIcon,
  Refresh as RefreshIcon,
  PersonOutline as PersonIcon,
  EventAvailable as DateIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { branchesList, branchesRequest, branchesApprove, branchesReject, branchesSuspend } from '../BaseURL';

const PRIMARY     = '#0e926a';
const PRIMARY_BG  = 'rgba(14,146,106,0.08)';
const BORDER      = '#e5e9ef';
const MUTED       = '#64748b';
const STRONG      = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem',
  fontWeight: 700,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
};

const STATUS_TONE = {
  pending:   { bg: 'rgba(234,179,8,0.10)',  fg: '#a16207', label: 'Pending approval' },
  active:    { bg: 'rgba(34,197,94,0.10)',  fg: '#15803d', label: 'Active' },
  rejected:  { bg: 'rgba(220,38,38,0.10)',  fg: '#b91c1c', label: 'Rejected' },
  suspended: { bg: 'rgba(100,116,139,0.12)',fg: '#475569', label: 'Suspended' },
};

const SubBranches = () => {
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(false);
  const [me, setMe]         = useState(null);

  // Request form state
  const [code, setCode]         = useState('');
  const [name, setName]         = useState('');
  const [location, setLocation] = useState('');
  const [address, setAddress]   = useState('');
  const [phone, setPhone]       = useState('');
  const [email, setEmail]       = useState('');
  const [submitting, setSub]    = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) setMe(JSON.parse(raw));
    } catch (_) {}
  }, []);

  const isSysAdmin = me?.role === 'System Administrator';
  const actorName  = me?.username || me?.name || '';

  const fetchData = useCallback(async () => {
    setLoad(true);
    try {
      const { data } = await axios.get(branchesList, { timeout: 12000 });
      setRows(data?.success ? (data.results || []) : []);
    } catch (err) {
      console.error('[sub-branches] list failed:', err.message || err);
      setRows([]);
    } finally {
      setLoad(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const reset = () => {
    setCode(''); setName(''); setLocation(''); setAddress(''); setPhone(''); setEmail('');
  };

  const submit = async () => {
    if (!code.trim() || !name.trim()) {
      Swal.fire({ icon: 'warning', title: 'Missing details', text: 'Code and name are required.' });
      return;
    }
    setSub(true);
    try {
      const { data } = await axios.post(branchesRequest, {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        location: location.trim() || null,
        address:  address.trim() || null,
        phone:    phone.trim()   || null,
        email:    email.trim()   || null,
        requestedBy: actorName || null,
      });
      if (data?.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Request submitted',
          html: `<p style="color:#475569;font-size:13px">Sub-branch <strong>${data.code}</strong> is waiting for a System Administrator to approve it before its users can log in.</p>`,
          confirmButtonColor: PRIMARY,
        });
        reset();
        fetchData();
      } else {
        Swal.fire({ icon: 'error', title: 'Could not submit', text: data?.message || 'Try again' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not submit', text: err?.response?.data?.message || err.message });
    } finally {
      setSub(false);
    }
  };

  const approve = async (row) => {
    const ok = await Swal.fire({
      icon: 'question',
      title: `Activate ${row.code}?`,
      html: `<p style="color:#475569;font-size:13px">This will allow staff registered under <strong>${row.name}</strong> to log in and start working on their own dataset.</p>`,
      showCancelButton: true, confirmButtonText: 'Activate', confirmButtonColor: PRIMARY,
    });
    if (!ok.isConfirmed) return;
    try {
      const { data } = await axios.post(branchesApprove, { branchId: row.id, approvedBy: actorName }, {
        headers: { 'x-actor': actorName || '' },
      });
      if (data?.success) { fetchData(); }
      else Swal.fire({ icon: 'error', title: 'Failed', text: data?.message || 'Try again' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || err.message });
    }
  };

  const reject = async (row) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: `Reject ${row.code}?`,
      input: 'text',
      inputLabel: 'Reason (optional)',
      inputPlaceholder: 'e.g. duplicate request',
      showCancelButton: true, confirmButtonText: 'Reject', confirmButtonColor: '#b91c1c',
    });
    if (!r.isConfirmed) return;
    try {
      const { data } = await axios.post(branchesReject, {
        branchId: row.id, reason: r.value || null, rejectedBy: actorName,
      }, { headers: { 'x-actor': actorName || '' } });
      if (data?.success) fetchData();
      else Swal.fire({ icon: 'error', title: 'Failed', text: data?.message || 'Try again' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || err.message });
    }
  };

  const suspend = async (row) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: `Suspend ${row.code}?`,
      html: `<p style="color:#475569;font-size:13px">Users at <strong>${row.name}</strong> will not be able to log in until the branch is reactivated.</p>`,
      input: 'text', inputLabel: 'Reason (optional)',
      showCancelButton: true, confirmButtonText: 'Suspend', confirmButtonColor: '#b45309',
    });
    if (!r.isConfirmed) return;
    try {
      const { data } = await axios.post(branchesSuspend, {
        branchId: row.id, reason: r.value || null, suspendedBy: actorName,
      }, { headers: { 'x-actor': actorName || '' } });
      if (data?.success) fetchData();
      else Swal.fire({ icon: 'error', title: 'Failed', text: data?.message || 'Try again' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || err.message });
    }
  };

  const reactivate = async (row) => {
    // Suspended → active uses the same approve endpoint (it accepts the
    // 'suspended' state as a valid source).
    const ok = await Swal.fire({
      icon: 'question',
      title: `Reactivate ${row.code}?`,
      showCancelButton: true, confirmButtonText: 'Reactivate', confirmButtonColor: PRIMARY,
    });
    if (!ok.isConfirmed) return;
    try {
      const { data } = await axios.post(branchesApprove, { branchId: row.id, approvedBy: actorName }, {
        headers: { 'x-actor': actorName || '' },
      });
      if (data?.success) fetchData();
      else Swal.fire({ icon: 'error', title: 'Failed', text: data?.message || 'Try again' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || err.message });
    }
  };

  const pending  = rows.filter(r => r.status === 'pending');
  const others   = rows.filter(r => r.status !== 'pending');

  return (
    <Box sx={{ p: 3, bgcolor: 'action.hover', minHeight: '100vh' }}>
      <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
            <BranchIcon sx={{ color: PRIMARY, fontSize: 18 }} />
            <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
              Administrative · Multi-tenancy
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: STRONG, lineHeight: 1.15 }}>
            Sub-Branches
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: MUTED, mt: 0.5 }}>
            Create and manage facility branches under this domain. Each active branch operates on its own dataset — patients, lab requests, admissions — while sharing the master catalogs.
          </Typography>
        </Box>
        <Tooltip title="Reload" arrow>
          <span>
            <IconButton size="small" onClick={fetchData} disabled={loading}
              sx={{ border: `1px solid ${BORDER}`, bgcolor: 'background.paper', color: MUTED }}>
              {loading ? <CircularProgress size={14} sx={{ color: PRIMARY }} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Submit a sub-branch request */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, bgcolor: 'background.paper', border: `1px solid ${BORDER}`, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <AddIcon sx={{ color: PRIMARY, fontSize: 20 }} />
          <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>Request a new sub-branch</Typography>
        </Stack>
        <Typography sx={{ fontSize: '0.78rem', color: MUTED, mb: 1.75 }}>
          Submitted requests stay <strong>pending</strong> until a System Administrator approves them (or until the row's status is updated directly in the <code>branches</code> table). Until then, no staff can log in under the new branch.
        </Typography>

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4} md={3}>
            <TextField fullWidth size="small" label="Branch code"
              value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
              helperText="2–16 chars, e.g. KUM, TAK1"
              inputProps={{ maxLength: 16, style: { textTransform: 'uppercase' } }} />
          </Grid>
          <Grid item xs={12} sm={8} md={5}>
            <TextField fullWidth size="small" label="Branch name"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Kumasi Annex" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth size="small" label="Location"
              value={location} onChange={(e) => setLocation(e.target.value)}
              placeholder="Town / city" />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField fullWidth size="small" label="Address"
              value={address} onChange={(e) => setAddress(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Phone"
              value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Email"
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
          <Button onClick={reset} disabled={submitting}
            sx={{ textTransform: 'none', color: MUTED }}>Reset</Button>
          <Button variant="contained" disableElevation onClick={submit} disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AddIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none', fontWeight: 700,
              bgcolor: PRIMARY, '&:hover': { bgcolor: '#0a7554' },
            }}>
            Submit request
          </Button>
        </Stack>
      </Paper>

      {/* Pending */}
      {pending.length > 0 && (
        <Paper elevation={0} sx={{ mb: 2.5, bgcolor: 'background.paper', border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, bgcolor: 'rgba(234,179,8,0.10)', borderBottom: `1px solid rgba(234,179,8,0.30)` }}>
            <Typography sx={{ ...LABEL_SX, color: '#a16207' }}>Pending approval ({pending.length})</Typography>
          </Box>
          <BranchTable
            rows={pending} isSysAdmin={isSysAdmin}
            onApprove={approve} onReject={reject} onSuspend={suspend} onReactivate={reactivate}
          />
        </Paper>
      )}

      {/* All branches */}
      <Paper elevation={0} sx={{ bgcolor: 'background.paper', border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, bgcolor: PRIMARY_BG, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>Branches</Typography>
          <Box sx={{ flex: 1 }} />
          {!isSysAdmin && (
            <Chip size="small" label="Read-only — only System Administrators can approve" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'background.paper', color: MUTED, border: `1px solid ${BORDER}` }} />
          )}
        </Box>
        {others.length === 0 && pending.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG }}>
              No branches recorded yet.
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.5 }}>
              The main branch should appear here after the server boots. Submit a sub-branch above to add one.
            </Typography>
          </Box>
        ) : (
          <BranchTable
            rows={others} isSysAdmin={isSysAdmin}
            onApprove={approve} onReject={reject} onSuspend={suspend} onReactivate={reactivate}
          />
        )}
      </Paper>
    </Box>
  );
};

const BranchTable = ({ rows, isSysAdmin, onApprove, onReject, onSuspend, onReactivate }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...LABEL_SX, bgcolor: 'action.hover' }}>Code</TableCell>
          <TableCell sx={{ ...LABEL_SX, bgcolor: 'action.hover' }}>Name</TableCell>
          <TableCell sx={{ ...LABEL_SX, bgcolor: 'action.hover' }}>Location</TableCell>
          <TableCell sx={{ ...LABEL_SX, bgcolor: 'action.hover' }}>Status</TableCell>
          <TableCell sx={{ ...LABEL_SX, bgcolor: 'action.hover' }}>Requested</TableCell>
          <TableCell sx={{ ...LABEL_SX, bgcolor: 'action.hover' }}>Approved</TableCell>
          <TableCell sx={{ ...LABEL_SX, bgcolor: 'action.hover' }}>Users</TableCell>
          <TableCell sx={{ ...LABEL_SX, bgcolor: 'action.hover' }} align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(r => {
          const tone = STATUS_TONE[r.status] || STATUS_TONE.pending;
          const isMain = r.id === 1;
          return (
            <TableRow key={r.id} hover>
              <TableCell sx={{ fontWeight: 700, color: STRONG, fontSize: '0.82rem' }}>
                {r.code}{isMain && <Chip size="small" label="HQ" sx={{ ml: 0.75, height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: PRIMARY_BG, color: PRIMARY, border: `1px solid rgba(14,146,106,0.25)` }} />}
              </TableCell>
              <TableCell sx={{ color: STRONG, fontSize: '0.82rem' }}>{r.name}</TableCell>
              <TableCell sx={{ color: MUTED, fontSize: '0.78rem' }}>{r.location || '—'}</TableCell>
              <TableCell>
                <Chip size="small" label={tone.label}
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: tone.bg, color: tone.fg, border: `1px solid ${tone.fg}30` }} />
              </TableCell>
              <TableCell sx={{ color: MUTED, fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {r.requested_at ? <Stack spacing={0.25}><span>{r.requested_at}</span><span style={{ fontSize: '0.7rem' }}>by {r.requested_by || '—'}</span></Stack> : '—'}
              </TableCell>
              <TableCell sx={{ color: MUTED, fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {r.approved_at ? <Stack spacing={0.25}><span>{r.approved_at}</span><span style={{ fontSize: '0.7rem' }}>by {r.approved_by || '—'}</span></Stack> : '—'}
              </TableCell>
              <TableCell sx={{ color: STRONG, fontSize: '0.82rem', fontWeight: 600 }}>{r.active_users || 0}</TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                  {isSysAdmin && r.status === 'pending' && (
                    <>
                      <Tooltip title="Approve" arrow>
                        <IconButton size="small" onClick={() => onApprove(r)} sx={{ color: '#15803d' }}>
                          <ApproveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject" arrow>
                        <IconButton size="small" onClick={() => onReject(r)} sx={{ color: '#b91c1c' }}>
                          <RejectIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {isSysAdmin && r.status === 'active' && !isMain && (
                    <Tooltip title="Suspend" arrow>
                      <IconButton size="small" onClick={() => onSuspend(r)} sx={{ color: '#b45309' }}>
                        <SuspendIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isSysAdmin && r.status === 'suspended' && (
                    <Tooltip title="Reactivate" arrow>
                      <IconButton size="small" onClick={() => onReactivate(r)} sx={{ color: '#15803d' }}>
                        <ApproveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isSysAdmin && r.status === 'rejected' && (
                    <Tooltip title="Approve anyway" arrow>
                      <IconButton size="small" onClick={() => onApprove(r)} sx={{ color: '#15803d' }}>
                        <ApproveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

export default SubBranches;
